import { isLocationEmpty, MINUTE } from '@caresend/utils';
import isEqual from 'lodash.isequal';
import { getStore } from '../../../helpers/initStore.js';

/**
 * Options Fetching Helpers for Scheduling Flow
 *
 * Manages the fetching state for two types of scheduling data:
 *
 * 1. SCHEDULING OPTIONS
 *    - When fetches happen
 *      - Prefetched in the background while user is still on address page
 *      - Fetched on the `SchedulingFlowTime` component if a new fetch is necessary
 *    - Cached for 5 minutes to prevent unnecessary refetches
 *
 * 2. LAB APPOINTMENT OPTIONS
 *    - When fetches happen
 *      - Fetched when a lab is selected (for labs without preset opening hours)
 *      - Always fetched asynchronously to keep labs selectable
 *    - Cached for 15 minutes (shorter duration for more real-time availability)
 *
 * LOADING OVERLAY LOGIC (`showLoadingForBackgroundFetch` flag)
 * - Bridges navigation between steps
 * - Set to true when the user presses the next button on a page where a prefetch will occur
 *     - Address page -> Time step
 *     - Time step -> Facility times step
 * - Set to false when the background fetch is complete
 */
/**
 * The threshold for when to reset scheduling options in the scheduling flow to
 * prevent stale options from being displayed.
 */
const RESET_SCHEDULING_OPTIONS_INTERVAL = 5 * MINUTE;
/**
 * The threshold for when to reset lab appointment times in the scheduling flow
 * to prevent stale times from being displayed.
 */
const RESET_LAB_APPOINTMENT_TIMES_INTERVAL = 15 * MINUTE;
/**
 * Returns the latest scheduling options fetch. This fetch is primarily used to
 * determine if we need to fetch new scheduling options.
 */
const getLatestSchedulingOptionsFetch = () => {
    const store = getStore();
    const { loadingSchedulingOptions } = store.state.schedulingFlow.localState;
    return Object.values(loadingSchedulingOptions !== null && loadingSchedulingOptions !== void 0 ? loadingSchedulingOptions : {}).sort((a, b) => b.timestamp - a.timestamp)[0];
};
const getLabAppointmentOptionsFetch = (placeID) => {
    const store = getStore();
    const { loadingLabAppointmentOptions } = store.state.schedulingFlow.localState;
    return loadingLabAppointmentOptions === null || loadingLabAppointmentOptions === void 0 ? void 0 : loadingLabAppointmentOptions[placeID];
};
/**
 * Used by the `schedulingFlow/getSchedulingOptions` action to determine if we
 * should begin fetching new scheduling options from the cloud or if we should
 * use the existing scheduling options.
 */
const getShouldGetNewSchedulingOptions = () => {
    const store = getStore();
    const newLocation = store.state.schedulingFlow.location;
    const isValidLocation = !isLocationEmpty(newLocation);
    const latestSchedulingOptionsFetch = getLatestSchedulingOptionsFetch();
    // If we don't have a previous fetch, we are on the address page on the mount of it
    // We'll only fetch if the location is valid
    if (!latestSchedulingOptionsFetch)
        return isValidLocation;
    const { location: lastLocation, isLoading, timestamp, } = latestSchedulingOptionsFetch;
    if (isLoading)
        return false;
    const hasSchedulingOptions = store.getters['schedulingFlow/getHasSchedulingOptions'];
    if (!hasSchedulingOptions)
        return true;
    const locationChanged = !isEqual(lastLocation, newLocation);
    if (locationChanged && isValidLocation)
        return true;
    const timeSinceLastFetch = Date.now() - timestamp;
    const isTimeWithinThreshold = timeSinceLastFetch < RESET_SCHEDULING_OPTIONS_INTERVAL;
    const areOptionsStale = isValidLocation && !isTimeWithinThreshold;
    return areOptionsStale;
};
/**
 * Returns true if we should get new lab appointment times for a given lab.
 */
const getShouldGetNewLabAppointmentTimes = (selectedDrawPlaceID) => {
    const store = getStore();
    const latestFetch = getLabAppointmentOptionsFetch(selectedDrawPlaceID);
    if (!latestFetch)
        return true;
    const { isLoading, timestamp } = latestFetch;
    if (isLoading)
        return false;
    const hasLabAppointmentOptions = store.getters['schedulingFlow/getHasLabAppointmentOptions'];
    if (!hasLabAppointmentOptions)
        return true;
    const timeSinceLastFetch = Date.now() - timestamp;
    const isTimeWithinThreshold = timeSinceLastFetch < RESET_LAB_APPOINTMENT_TIMES_INTERVAL;
    const areTimesStale = !isTimeWithinThreshold;
    return areTimesStale;
};

export { RESET_LAB_APPOINTMENT_TIMES_INTERVAL, RESET_SCHEDULING_OPTIONS_INTERVAL, getLabAppointmentOptionsFetch, getLatestSchedulingOptionsFetch, getShouldGetNewLabAppointmentTimes, getShouldGetNewSchedulingOptions };
